import { computed, watch, type Component, type Ref } from 'vue'
import {
  useAsyncMessages,
  type UseAsyncMessagesReturn
} from './useAsyncMessages'
import type {
  FormFieldMessage,
  FormFieldMessagesAsyncHandler
} from './form-field-messages-types'

/**
 * Shared glue between a `Form*Field` component and `useAsyncMessages`.
 *
 * Responsibilities:
 * 1. Reads the field value reactively off the tanstack store.
 * 2. Wires the `messagesAsync` handler with debounce + cancellation.
 * 3. Combines async-loaded messages with any imperative `messages` prop.
 * 4. **Promotes `variant: 'error'` messages into tanstack field state**
 *    via `setFieldMeta(name, { errorMap: { onChange: ... } })` so the
 *    same errors block `form.handleSubmit()` and feed `canSubmit`.
 *    Writes to `errorMap.onChange` (separate slot from zod's
 *    `errorMap.onSubmit`) so the two don't clobber each other.
 *
 * Each field component calls this once in setup; the returned
 * `combinedMessages` goes to `FormFieldLayout`'s `:messages` prop, and
 * `isLoading` can be shown next to the label.
 */
export function useFormAsyncMessages(options: {
  form: { Field: Component }
  name: string
  messages: Ref<FormFieldMessage[] | undefined>
  messagesAsync: Ref<FormFieldMessagesAsyncHandler | undefined>
  messagesAsyncDebounceMs: Ref<number | undefined>
}): {
  combinedMessages: Ref<FormFieldMessage[]>
  isLoading: Ref<boolean>
  clear: UseAsyncMessagesReturn['clear']
} {
  const formApi = options.form as unknown as {
    useStore: <T>(
      selector: (state: { values: Record<string, unknown> }) => T
    ) => { value: T }
    setFieldMeta: (
      name: string,
      updater: (prev: FieldMetaShape) => FieldMetaShape
    ) => void
  }
  // Read the raw value off the store. For non-string field values
  // (numbers, booleans) the selector stringifies so the async handler
  // signature stays consistent across fields. Handlers that care about
  // the real shape can re-parse, or — for booleans — just skip.
  const valueRef = formApi.useStore((s) => {
    const raw = s.values[options.name]
    if (raw === null || raw === undefined) return ''
    return typeof raw === 'string' ? raw : String(raw)
  })

  const debounceRef = computed(
    () => options.messagesAsyncDebounceMs.value ?? 400
  )
  const enabledRef = computed(() => !!options.messagesAsync.value)

  const {
    messages: asyncMessages,
    isLoading,
    clear
  } = useAsyncMessages({
    valueSource: () => valueRef.value,
    handler: options.messagesAsync,
    debounceMs: debounceRef,
    enabled: enabledRef
  })

  const combinedMessages = computed<FormFieldMessage[]>(() => [
    ...asyncMessages.value,
    ...(options.messages.value ?? [])
  ])

  // Auto-promote `variant: 'error'` entries from the combined messages
  // into tanstack's `errorMap.onChange` slot so they count toward
  // `canSubmit` and are surfaced by `form.handleSubmit()`. We write to
  // `onChange` specifically because the zod schema lives on `onSubmit` —
  // keeping the two sources in separate slots prevents one from
  // overwriting the other on each validation cycle.
  watch(
    () => combinedMessages.value.filter((m) => m.variant === 'error'),
    (errorMessages, prevErrorMessages) => {
      const curr = errorMessages.map((m) => m.message)
      const prev = (prevErrorMessages ?? []).map((m) => m.message)
      if (curr.length === prev.length && curr.every((m, i) => m === prev[i])) {
        return
      }
      const nextValue: string | undefined =
        curr.length > 0 ? curr.join(', ') : undefined
      formApi.setFieldMeta(options.name, (prevMeta) => {
        const nextErrorMap = {
          ...prevMeta.errorMap,
          onChange: nextValue
        }
        return {
          ...prevMeta,
          errorMap: nextErrorMap,
          errors: buildErrorsFromMap(nextErrorMap, prevMeta.errors)
        }
      })
    },
    { deep: true, immediate: true }
  )

  return { combinedMessages, isLoading, clear }
}

/** Loose shape of tanstack's `fieldMeta` entry — we only touch errorMap + errors. */
interface FieldMetaShape {
  errors?: unknown[]
  errorMap?: {
    onChange?: string
    onBlur?: string
    onSubmit?: string
    [key: string]: string | undefined
  }
  [key: string]: unknown
}

/**
 * Rebuild `meta.errors` from `errorMap` while preserving any non-mapped
 * entries (unlikely, but defensive). Tanstack normally derives `errors`
 * from `errorMap` itself, but some versions don't when we mutate via
 * `setFieldMeta` — so we recompute explicitly.
 */
function buildErrorsFromMap(
  errorMap: FieldMetaShape['errorMap'],
  prevErrors: unknown[] | undefined
): unknown[] {
  const mapped = Object.values(errorMap ?? {}).filter(
    (v): v is string => typeof v === 'string' && v.length > 0
  )
  if (!prevErrors || prevErrors.length === 0) return mapped
  // Keep any existing non-string (structured) error entries — rare but
  // protect against tanstack putting richer shapes here.
  const structured = prevErrors.filter((e) => typeof e !== 'string')
  return [...structured, ...mapped]
}
